/**
 * The trial's matching, which is SIMULATED and says so.
 *
 * This is a deterministic keyword/experience/education model over the twelve
 * synthetic candidates. It is not Bedrock, it is not the deep-matching handler,
 * and it does not touch Nexxt. It exists so a prospect can paste a real job
 * description and watch the flow behave, which is the thing being validated.
 *
 * Ported from the approved prototype with the arithmetic unchanged, so the
 * trial ranks candidates identically. Two things are named honestly here that
 * the prototype only explained in its footer:
 *
 *   - `jitter` is a spread term, not a signal. It exists so twelve candidates
 *     do not land on four identical scores. It is derived from the candidate id
 *     and would be indefensible in a real scorer.
 *   - `simulatedDistanceMiles` is a hash, not a distance. Nothing is geocoded.
 *
 * Both are kept because the prototype's behaviour is what was approved, and
 * both are labelled so nobody later mistakes them for the real pipeline.
 */

import {
  CANDIDATES,
  SKILL_DICT,
  Education,
  SyntheticCandidate,
} from '../data/candidates';

/** How many ranked results the trial shows. */
export const RESULT_COUNT = 5;

const EDU_RANK: Record<Education, number> = {
  'high school': 0,
  certification: 1,
  bachelor: 2,
};

export interface JdSignals {
  /** Vocabulary terms found in the pasted text, in dictionary order. */
  skills: string[];
  /** Years of experience the JD asks for, or null if it does not say. */
  requiredYears: number | null;
  /** Education the JD asks for, or null if it does not say. */
  requiredEducation: Education | null;
}

export interface ScoredCandidate {
  candidate: SyntheticCandidate;
  score: number;
  /** Which of the JD's recognised skills this candidate has. */
  matched: string[];
}

export interface MatchResult {
  signals: JdSignals;
  ranked: ScoredCandidate[];
}

/**
 * Read what we can out of a pasted job description.
 *
 * Substring matching against a fixed vocabulary. It finds "python" inside
 * "Python 3" and equally inside "not python", which is the honest limit of a
 * keyword parser and the reason the UI calls this "recognised" rather than
 * "understood".
 */
export const readJdSignals = (jd: string): JdSignals => {
  const lower = jd.toLowerCase();

  const yearsMatch = jd.match(/(\d+)\+?\s*(?:years|yrs)/i);
  const requiredYears = yearsMatch ? parseInt(yearsMatch[1], 10) : null;

  let requiredEducation: Education | null = null;
  if (lower.includes('bachelor') || lower.includes('degree')) {
    requiredEducation = 'bachelor';
  } else if (lower.includes('certif')) {
    requiredEducation = 'certification';
  }

  return {
    skills: SKILL_DICT.filter((skill) => lower.includes(skill)),
    requiredYears,
    requiredEducation,
  };
};

/**
 * Score one candidate against the JD's signals.
 *
 * Weighted 60% skills / 25% experience / 15% education, clamped to 28-99. When
 * the JD yielded no recognised skills the skill term falls back to a fraction
 * of the candidate's own breadth, halved -- so a no-keyword JD ranks by
 * experience rather than returning nothing, which is what the UI tells the user
 * is happening.
 */
export const scoreCandidate = (
  candidate: SyntheticCandidate,
  signals: JdSignals,
): ScoredCandidate => {
  const matched = candidate.skills.filter((s) => signals.skills.includes(s));

  const skillRatio = signals.skills.length
    ? matched.length / signals.skills.length
    : (matched.length / candidate.skills.length) * 0.5;

  let expScore: number;
  if (signals.requiredYears) {
    expScore =
      candidate.years >= signals.requiredYears
        ? 1
        : Math.max(0, candidate.years / signals.requiredYears);
  } else {
    expScore = Math.min(1, candidate.years / 6);
  }

  const eduScore = signals.requiredEducation
    ? EDU_RANK[candidate.education] >= EDU_RANK[signals.requiredEducation]
      ? 1
      : 0.5
    : 1;

  const raw = skillRatio * 0.6 + expScore * 0.25 + eduScore * 0.15;

  // Spread, not signal. See the module comment.
  const jitter =
    (candidate.id.charCodeAt(0) * 13 + signals.skills.length * 7) % 5;

  const score = Math.round(Math.min(99, Math.max(28, raw * 100 + jitter - 2)));

  return { candidate, score, matched };
};

/** Rank the synthetic set against a pasted JD and take the top few. */
export const runMatch = (jd: string): MatchResult => {
  const signals = readJdSignals(jd);
  const ranked = CANDIDATES.map((c) => scoreCandidate(c, signals))
    .sort((a, b) => b.score - a.score)
    .slice(0, RESULT_COUNT);
  return { signals, ranked };
};

/**
 * A stand-in for distance from the office.
 *
 * NOT a distance. A hash of the candidate id and the typed location, mapped
 * into 2-44 miles. Deterministic so the same inputs give the same number and
 * the screen does not reshuffle on re-render. Real geocoding against the office
 * address is what this stands in for.
 */
export const simulatedDistanceMiles = (
  candidate: SyntheticCandidate,
  officeLocation: string,
): number => {
  const base = candidate.id.charCodeAt(0) * 3;
  const loc = (officeLocation || 'unspecified office').toLowerCase();
  const locHash = loc.split('').reduce((acc, ch) => acc + ch.charCodeAt(0), 0);
  return ((base + locHash) % 43) + 2;
};

/**
 * Acronyms in the skill vocabulary that must not be title-cased. Without this,
 * "sql" renders as "Sql" and "osha" as "Osha", which reads as a typo to anyone
 * who works in the field the term belongs to -- which is exactly the audience
 * for the trades and technical samples.
 */
const ACRONYMS = new Set([
  'sql',
  'aws',
  'api',
  'crm',
  'seo',
  'osha',
  'hvac',
  'cdl',
]);

/** Title-case a vocabulary term for display, leaving acronyms uppercase. */
export const capitalise = (s: string): string =>
  s
    .split(' ')
    .map((word) =>
      ACRONYMS.has(word.toLowerCase())
        ? word.toUpperCase()
        : word.replace(/\b\w/g, (c) => c.toUpperCase()),
    )
    .join(' ');
